import {
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useTransitionStyles
} from '@floating-ui/react'
import { faFilter } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { type ReactNode, useState } from 'react'
import { Checkbox } from '~/components/Form'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { filterDropdown, filterOption } from './TypeFilter.css'

export type TypeFilterOption<T extends string = string> = {
  label: ReactNode
  value: T
}

type TypeFilterProps<T extends string> = {
  options: TypeFilterOption<T>[]
  selectedTypes: Set<T>
  onTypesChange: (types: T[]) => void
  buttonText?: string
  buttonTitle?: string
  buttonSize?: 'extraSmall' | 'small'
  checkAllWhenEmpty?: boolean
}

export const TypeFilter = <T extends string = string>({
  options,
  selectedTypes,
  onTypesChange,
  buttonText = 'Filtres',
  buttonTitle,
  buttonSize = 'extraSmall',
  checkAllWhenEmpty = true
}: TypeFilterProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedTypesState, setSelectedTypesState] =
    useState<Set<T>>(selectedTypes)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate
  })

  const dismiss = useDismiss(context)
  const { getFloatingProps } = useInteractions([dismiss])

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 150,
    initial: {
      opacity: 0,
      transform: 'scale(0.95)'
    },
    open: {
      opacity: 1,
      transform: 'scale(1)'
    },
    close: {
      opacity: 0,
      transform: 'scale(0.95)'
    }
  })

  const activeCount = selectedTypes.size

  const handleButtonClick = () => {
    setIsOpen(!isOpen)
  }

  return (
    <div style={{ position: 'relative' }}>
      <div ref={refs.setReference}>
        <Button
          variation={ButtonVariation.Neutral}
          onClick={handleButtonClick}
          buttonSize={buttonSize}
          title={buttonTitle}
          aria-label={buttonTitle}
        >
          <FontAwesomeIcon icon={faFilter} />
          {buttonText}
          {activeCount > 0 && ` (${activeCount})`}
        </Button>
      </div>

      {isMounted && (
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            zIndex: 9999
          }}
          {...getFloatingProps()}
        >
          <div style={{ ...transitionStyles }} className={filterDropdown}>
            {options.map((option) => (
              <label key={option.value} className={filterOption}>
                <Checkbox
                  checked={
                    selectedTypesState.has(option.value) ||
                    (checkAllWhenEmpty && selectedTypesState.size === 0)
                  }
                  onChange={() => {
                    const newSelectedTypesState = new Set(selectedTypesState)
                    if (checkAllWhenEmpty && selectedTypesState.size === 0) {
                      // If no type was selected, select all other types (the user juste clicked on a checked checkbox)
                      options.forEach((o) => {
                        if (o.value !== option.value) {
                          newSelectedTypesState.add(o.value)
                        }
                      })
                    } else if (selectedTypesState.has(option.value)) {
                      newSelectedTypesState.delete(option.value)
                    } else {
                      newSelectedTypesState.add(option.value)
                    }
                    setSelectedTypesState(newSelectedTypesState)
                    onTypesChange(Array.from(newSelectedTypesState))
                  }}
                />
                {option.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
